import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { PlayerWidgetType, SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedPlayerWidget } from 'fym-common/src/db/serializersTypes';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { TypedMemo } from '../../../common/types';
import { useGetCatalogQuery } from '../../../../redux/api/fymAPI';
import { LogoServices } from '../../widgets/views/playerWidget/forms/LogoServices';
import { SMLSegmented, SegmentedOption } from '../controls/SMLSegmented';
import { blockSummaryMessages } from '../blocks/blockSummaryMessages';
import { BlockAutosave } from './useBlockAutosave';
import { formMessages } from './formMessages';
import { normalizeAddress } from './normalizeAddress';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for every block form */
import styles from './SMLBlockForm.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The player data version the web editor writes (as the old Player form). */
const PLAYER_WIDGET_VERSION = 2;

interface PlayerBlockFormProps {
  widget: SerializedPlayerWidget;
  accountId: number;
  autosave: BlockAutosave;
}

/**
 * The Player block (design d4): the song (the Spotify link of a Catalog song, or a pasted link) and
 * the size. `{ url, type, version }`; the server makes the embed address.
 */
const PlayerBlockFormComponent: React.FC<PlayerBlockFormProps> = ({ widget, accountId, autosave }) => {
  const intl = useIntl();
  const [url, setUrl] = useState(widget.data.url);
  const [size, setSize] = useState<PlayerWidgetType>(widget.data.type ?? PlayerWidgetType.normal);
  const { save, markInvalid } = autosave;
  const { data } = useGetCatalogQuery({ accountId });
  const songs = useMemo(
    () =>
      (data?.items ?? [])
        .map((item) => ({
          uuid: item.uuid,
          title: item.title,
          url: item.serviceLinks?.[MusicAPIIntegrationID.Spotify],
        }))
        .filter((item): item is { uuid: string; title: string; url: string } => typeof item.url === 'string'),
    [data]
  );
  const isBad = normalizeAddress(url) === null;

  const commit = useCallback(
    (nextUrl: string, nextSize: PlayerWidgetType) => {
      const address = normalizeAddress(nextUrl);
      if (address === null) {
        markInvalid();
        return;
      }
      save({ url: address, type: nextSize, version: PLAYER_WIDGET_VERSION });
    },
    [markInvalid, save]
  );

  const onUrl = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUrl(event.target.value);
      commit(event.target.value, size);
    },
    [commit, size]
  );
  const onSong = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const song = songs.find((one) => one.uuid === event.target.value);
      if (song !== undefined) {
        setUrl(song.url);
        commit(song.url, size);
      }
    },
    [commit, size, songs]
  );
  const onSize = useCallback(
    (next: PlayerWidgetType) => {
      setSize(next);
      commit(url, next);
    },
    [commit, url]
  );

  const sizeOptions = useMemo<SegmentedOption<PlayerWidgetType>[]>(
    () => [
      { value: PlayerWidgetType.normal, label: intl.formatMessage(blockSummaryMessages.sizeNormal) },
      { value: PlayerWidgetType.compact, label: intl.formatMessage(blockSummaryMessages.sizeCompact) },
    ],
    [intl]
  );
  const pickedSong = songs.find((song) => song.url === url)?.uuid ?? '';

  return (
    <div className={styles.form} data-testid={`form-${SmartLinkWidgetType.player}`}>
      <div className={styles.field}>
        <span className={styles.label}>{intl.formatMessage(formMessages.playerSong)}</span>
        {songs.length > 0 && (
          <select
            className={styles.input}
            value={pickedSong}
            onChange={onSong}
            aria-label={intl.formatMessage(formMessages.playerFromCatalog)}
          >
            <option value="">{intl.formatMessage(formMessages.playerChooseSong)}</option>
            {songs.map((song) => (
              <option key={song.uuid} value={song.uuid}>
                {song.title}
              </option>
            ))}
          </select>
        )}
        <input
          className={clsx(styles.input, isBad && styles.inputBad)}
          value={url}
          onChange={onUrl}
          inputMode="url"
          aria-label={intl.formatMessage(formMessages.playerLink)}
          aria-invalid={isBad}
          placeholder="https://open.spotify.com/track/…"
          data-testid="player-block-url"
        />
        <p className={isBad ? styles.helpBad : styles.help}>
          {intl.formatMessage(isBad ? formMessages.playerBad : formMessages.playerPaste)}
        </p>
        <div className={styles.buttonRow}>
          <LogoServices />
        </div>
      </div>
      <div className={styles.field}>
        <span className={styles.label}>{intl.formatMessage(formMessages.size)}</span>
        <SMLSegmented
          options={sizeOptions}
          value={size}
          onChange={onSize}
          ariaLabel={intl.formatMessage(formMessages.size)}
        />
        <p className={styles.help}>{intl.formatMessage(formMessages.sizeHelp)}</p>
      </div>
    </div>
  );
};

export const PlayerBlockForm = TypedMemo(PlayerBlockFormComponent);
